/**
 * Losi Inference catalog
 *
 * Product-facing voice/brain options powered by LiveKit Inference.
 * UI label is always "Losi Inference" — never expose LiveKit to users.
 */

export const LOSI_INFERENCE_PROVIDER_ID = 'losi-inference' as const
export const LOSI_INFERENCE_PROVIDER_LABEL = 'Losi Inference'
export const LOSI_VOICE_AGENT_NAME = 'losi-voice'

/** Default voice LLM — latency-optimized (LiveKit recommended for agents). */
export const DEFAULT_LOSI_INFERENCE_LLM = 'google/gemma-4-31b-it'

/** Best-quality brain option in the picker. */
export const BEST_LOSI_INFERENCE_LLM = 'openai/gpt-5.6-terra'

/** Default STT for voice sessions. */
export const DEFAULT_LOSI_INFERENCE_STT = 'assemblyai/universal-3-5-pro'

/** Default TTS voice descriptor (`model:voiceId`). */
export const DEFAULT_LOSI_INFERENCE_VOICE =
  'cartesia/sonic-3:9626c31c-bec5-4cca-baa8-f8ba9e84c8bc' // Jacqueline

export type LosiInferenceBrain = {
  id: string
  label: string
  description: string
  /** Shown as the recommended default for voice. */
  isVoiceDefault?: boolean
  /** Shown as the smartest / best option. */
  isBest?: boolean
}

export type LosiInferenceVoice = {
  id: string
  label: string
  description: string
  language: string
  /** Family label for grouping in the picker (never a vendor "provider"). */
  family: string
}

export type LosiInferenceStt = {
  id: string
  label: string
  description: string
}

export const LOSI_INFERENCE_BRAINS: LosiInferenceBrain[] = [
  {
    id: DEFAULT_LOSI_INFERENCE_LLM,
    label: 'Losi Voice',
    description: 'Fast, natural conversation — best default for talking.',
    isVoiceDefault: true
  },
  {
    id: BEST_LOSI_INFERENCE_LLM,
    label: 'Losi Terra',
    description: 'Strongest reasoning for complex voice tasks.',
    isBest: true
  },
  {
    id: 'openai/gpt-5.6-sol',
    label: 'Losi Sol',
    description: 'High-capability balance of speed and depth.'
  },
  {
    id: 'openai/gpt-5.6-luna',
    label: 'Losi Luna',
    description: 'Lighter GPT-5.6 for snappy replies.'
  },
  {
    id: 'openai/gpt-5.5',
    label: 'Losi 5.5',
    description: 'Strong general model.'
  },
  {
    id: 'google/gemini-3.8-flash',
    label: 'Losi Flash',
    description: 'Very fast multimodal reasoning.'
  },
  {
    id: 'xai/grok-4.7',
    label: 'Losi Grok',
    description: 'Bold, conversational style.'
  },
  {
    id: 'deepseek-ai/deepseek-v4.1-flash',
    label: 'Losi Spark',
    description: 'Efficient high-throughput brain.'
  }
]

/** Curated voices from LiveKit Inference suggested list — branded as Losi. */
export const LOSI_INFERENCE_VOICES: LosiInferenceVoice[] = [
  {
    id: 'cartesia/sonic-3:9626c31c-bec5-4cca-baa8-f8ba9e84c8bc',
    label: 'Jacqueline',
    description: 'Confident young American female',
    language: 'en-US',
    family: 'Natural'
  },
  {
    id: 'cartesia/sonic-3:a167e0f3-df7e-4d52-a9c3-f949145efdab',
    label: 'Blake',
    description: 'Energetic American adult male',
    language: 'en-US',
    family: 'Natural'
  },
  {
    id: 'cartesia/sonic-3:f31cc6a7-c1e8-4764-980c-60a361443dd1',
    label: 'Robyn',
    description: 'Neutral mature Australian female',
    language: 'en-AU',
    family: 'Natural'
  },
  {
    id: 'fishaudio/s2.1-pro:fa4c9eb3dccc4806b382b40d61c6b10a',
    label: 'Adrian',
    description: 'Friendly casual American male',
    language: 'en-US',
    family: 'Warm'
  },
  {
    id: 'fishaudio/s2.1-pro:9a9cf47702da476aa4629e2506d4a857',
    label: 'Hannah',
    description: 'Conversational specialist',
    language: 'en-US',
    family: 'Warm'
  },
  {
    id: 'fishaudio/s2.1-pro:e3cd384158934cc9a01029cd7d278634',
    label: 'Laura',
    description: 'Confident female narrator',
    language: 'en-US',
    family: 'Warm'
  },
  {
    id: 'fishaudio/s2.1-pro:933563129e564b19a115bedd57b7406a',
    label: 'Sarah',
    description: 'Engaged speaker',
    language: 'en-US',
    family: 'Warm'
  },
  {
    id: 'deepgram/aura-2:athena',
    label: 'Athena',
    description: 'Smooth professional female',
    language: 'en-US',
    family: 'Clear'
  },
  {
    id: 'deepgram/aura-2:apollo',
    label: 'Apollo',
    description: 'Comfortable casual male',
    language: 'en-US',
    family: 'Clear'
  },
  {
    id: 'deepgram/aura-2:odysseus',
    label: 'Odysseus',
    description: 'Calm professional male',
    language: 'en-US',
    family: 'Clear'
  },
  {
    id: 'rime/coda:astra',
    label: 'Astra',
    description: 'Chipper upbeat American female',
    language: 'en-US',
    family: 'Bright'
  },
  {
    id: 'rime/coda:luna',
    label: 'Luna',
    description: 'Chill but excitable American female',
    language: 'en-US',
    family: 'Bright'
  },
  {
    id: 'inworld/inworld-tts-2:Ashley',
    label: 'Ashley',
    description: 'Warm natural American female',
    language: 'en-US',
    family: 'Expressive'
  },
  {
    id: 'inworld/inworld-tts-2:Edward',
    label: 'Edward',
    description: 'Fast-talking emphatic American male',
    language: 'en-US',
    family: 'Expressive'
  },
  {
    id: 'inworld/inworld-tts-2:Olivia',
    label: 'Olivia',
    description: 'Upbeat friendly British female',
    language: 'en-GB',
    family: 'Expressive'
  },
  {
    id: 'xai/tts-1:ara',
    label: 'Ara',
    description: 'Warm and friendly',
    language: 'en-US',
    family: 'Cosmic'
  },
  {
    id: 'xai/tts-1:eve',
    label: 'Eve',
    description: 'Energetic and upbeat',
    language: 'en-US',
    family: 'Cosmic'
  },
  {
    id: 'xai/tts-1:leo',
    label: 'Leo',
    description: 'Authoritative and strong',
    language: 'en-US',
    family: 'Cosmic'
  },
  {
    id: 'gradium/default:4SZHfMpw-p46Ywgs',
    label: 'Harper',
    description: 'Modern confident American female',
    language: 'en-US',
    family: 'Modern'
  },
  {
    id: 'gradium/default:6MFfc37kq0sBjBjy',
    label: 'Sterling',
    description: 'Warm energetic American male',
    language: 'en-US',
    family: 'Modern'
  }
]

export const LOSI_INFERENCE_STT: LosiInferenceStt[] = [
  {
    id: DEFAULT_LOSI_INFERENCE_STT,
    label: 'Losi Hear',
    description: 'Accurate streaming transcription (default).'
  },
  {
    id: 'deepgram/flux-general-en',
    label: 'Losi Hear Fast',
    description: 'Lower-latency English transcription.'
  },
  {
    id: 'deepgram/nova-3',
    label: 'Losi Hear Multi',
    description: 'Multilingual Nova-3 transcription.'
  }
]

export type LosiInferenceSessionOptions = {
  llm: string
  voice: string
  stt: string
  /** Speaking-rate multiplier (1.0 = normal). Optional — worker applies provider options. */
  speed?: number
}

export function getDefaultLosiInferenceOptions(): LosiInferenceSessionOptions {
  return {
    llm: DEFAULT_LOSI_INFERENCE_LLM,
    voice: DEFAULT_LOSI_INFERENCE_VOICE,
    stt: DEFAULT_LOSI_INFERENCE_STT
  }
}

export function isKnownLosiInferenceLlm(id: string) {
  return LOSI_INFERENCE_BRAINS.some(brain => brain.id === id)
}

export function isKnownLosiInferenceVoice(id: string) {
  return LOSI_INFERENCE_VOICES.some(voice => voice.id === id)
}

export function isKnownLosiInferenceStt(id: string) {
  return LOSI_INFERENCE_STT.some(stt => stt.id === id)
}

export function resolveLosiInferenceOptions(
  input?: Partial<LosiInferenceSessionOptions> | null
): LosiInferenceSessionOptions {
  const defaults = getDefaultLosiInferenceOptions()
  const llm =
    input?.llm && isKnownLosiInferenceLlm(input.llm) ? input.llm : defaults.llm
  const voice =
    input?.voice && isKnownLosiInferenceVoice(input.voice)
      ? input.voice
      : defaults.voice
  const stt =
    input?.stt && isKnownLosiInferenceStt(input.stt) ? input.stt : defaults.stt
  const speed =
    typeof input?.speed === 'number' && Number.isFinite(input.speed)
      ? Math.max(0.25, Math.min(4, input.speed))
      : undefined
  return { llm, voice, stt, ...(speed != null ? { speed } : {}) }
}

/** Split `provider/model:voiceId` into Inference TTS fields. */
export function parseLosiInferenceVoiceDescriptor(descriptor: string): {
  model: string
  voice: string
} {
  const colon = descriptor.indexOf(':')
  if (colon <= 0) {
    return { model: descriptor, voice: descriptor }
  }
  return {
    model: descriptor.slice(0, colon),
    voice: descriptor.slice(colon + 1).trim()
  }
}

export function getLosiInferenceCatalogPayload() {
  return {
    provider: LOSI_INFERENCE_PROVIDER_ID,
    label: LOSI_INFERENCE_PROVIDER_LABEL,
    defaults: getDefaultLosiInferenceOptions(),
    brains: LOSI_INFERENCE_BRAINS,
    voices: LOSI_INFERENCE_VOICES,
    stt: LOSI_INFERENCE_STT,
    chatRealtimeDefaults: getChatRealtimeLosiInferenceOptions(),
    chatBrainCascade: getChatRealtimeBrainCascade()
  }
}

/** Surfaces that can start a Losi Inference realtime session. */
export type LosiInferenceSurface =
  | 'chat'
  | 'sidekick'
  | 'sidecar'
  | 'nexus'
  | 'dictation'

export type LosiInferenceInteractionMode =
  | 'full'
  | 'text_only'
  | 'speak_only'
  | 'transcribe_only'

export function isLosiInferenceSurface(value: unknown): value is LosiInferenceSurface {
  return (
    value === 'chat' ||
    value === 'sidekick' ||
    value === 'sidecar' ||
    value === 'nexus' ||
    value === 'dictation'
  )
}

export function isLosiInferenceInteractionMode(
  value: unknown
): value is LosiInferenceInteractionMode {
  return (
    value === 'full' ||
    value === 'text_only' ||
    value === 'speak_only' ||
    value === 'transcribe_only'
  )
}

/**
 * Chat / Sidekick realtime: start at best brain, then cascade down the catalog.
 * Demo Call / Nexus phone keep latency-first defaults via getDefaultLosiInferenceOptions().
 */
export function getChatRealtimeBrainCascade(): string[] {
  const ids = LOSI_INFERENCE_BRAINS.map(brain => brain.id)
  const bestFirst = [
    BEST_LOSI_INFERENCE_LLM,
    ...ids.filter(id => id !== BEST_LOSI_INFERENCE_LLM)
  ]
  return Array.from(new Set(bestFirst))
}

export function getChatRealtimeLosiInferenceOptions(): LosiInferenceSessionOptions {
  const cascade = getChatRealtimeBrainCascade()
  return {
    llm: cascade[0] || BEST_LOSI_INFERENCE_LLM,
    voice: DEFAULT_LOSI_INFERENCE_VOICE,
    stt: DEFAULT_LOSI_INFERENCE_STT
  }
}

export function resolveChatRealtimeLosiInferenceOptions(
  input?: Partial<LosiInferenceSessionOptions> | null
): LosiInferenceSessionOptions {
  const defaults = getChatRealtimeLosiInferenceOptions()
  const llm =
    input?.llm && isKnownLosiInferenceLlm(input.llm) ? input.llm : defaults.llm
  const voice =
    input?.voice && isKnownLosiInferenceVoice(input.voice)
      ? input.voice
      : defaults.voice
  const stt =
    input?.stt && isKnownLosiInferenceStt(input.stt) ? input.stt : defaults.stt
  return { llm, voice, stt }
}
